import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Section } from "../components/Common";
import PractitionerRecommendations from "../components/PractitionerRecommendations";
import HeroGoldOverlay from "../components/HeroGoldOverlay";
import MotivationChip from "../components/MotivationChip";
import AppDownloadSection from "../components/AppDownloadSection";
import MemberBenefitsStrip from "../components/MemberBenefitsStrip";
import WelcomeBanner from "../components/WelcomeBanner";
import BRAND from "../constants/brand";
import {
  ArrowRight, Sparkles, Heart, Users, Briefcase, ShoppingBag,
} from "lucide-react";

/**
 * DONiTSH!T Homepage — Ecosystem-First IA.
 *
 * Priority order (locked with user):
 *   1. Wellness  2. Relationships  3. Families & Parenting
 *   4. Business & Corporate  5. Shop  6. Ask Donny  7. Dosha Test
 *
 * Goal: feel like a content-rich wellness & lifestyle destination,
 * NOT a Dosha-test landing page. Five large gateway cards lead the
 * user into the ecosystem; the floating bubble CTA carries Dosha.
 */
export default function HomePage() {
  const { t } = useTranslation();

  // Five primary gateway cards
  const gateways = [
    {
      to: "/wellness",
      title: t("home.gateway_wellness"),
      blurb: t("home.gateway_wellness_blurb"),
      image: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=1400&q=80",
      icon: Sparkles,
      eyebrow: t("home.gateway_wellness_eyebrow"),
    },
    {
      to: "/relationships",
      title: t("home.gateway_relationships"),
      blurb: t("home.gateway_relationships_blurb"),
      image: "https://images.unsplash.com/photo-1518621736915-f3b1c41bfd00?auto=format&fit=crop&w=1400&q=80",
      icon: Heart,
      eyebrow: t("home.gateway_relationships_eyebrow"),
    },
    {
      to: "/families",
      title: t("home.gateway_families"),
      blurb: t("home.gateway_families_blurb"),
      image: "https://images.unsplash.com/photo-1609220136736-443140cffec6?auto=format&fit=crop&w=1400&q=80",
      icon: Users,
      eyebrow: t("home.gateway_families_eyebrow"),
    },
    {
      to: "/business",
      title: t("home.gateway_business"),
      blurb: t("home.gateway_business_blurb"),
      image: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=1400&q=80",
      icon: Briefcase,
      eyebrow: t("home.gateway_business_eyebrow"),
    },
    {
      to: "/shop",
      title: t("home.gateway_shop"),
      blurb: t("home.gateway_shop_blurb"),
      image: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1400&q=80",
      icon: ShoppingBag,
      eyebrow: t("home.gateway_shop_eyebrow"),
    },
  ];

  // Motivation chips — fully localized via en.js home.chips array
  const chips = (t("home.chips", { returnObjects: true }) || []).map((c) => ({
    label: c.label,
    quote: c.quote,
    ctaLabel: c.cta_label,
    ctaTo: c.cta_to,
  }));

  return (
    <div data-testid="page-home">
      {/* Logged-in welcome banner (renders nothing for anonymous users) */}
      <WelcomeBanner />

      {/* ============================================================ */}
      {/* HERO — official brand artwork + premium gold overlay + new copy */}
      {/* ============================================================ */}
      <section className="relative overflow-hidden border-b border-white/10" data-testid="hero">
        <div className="absolute inset-0">
          <img
            src={BRAND.HERO}
            alt={t("home.hero_alt")}
            className="w-full h-full object-cover object-center"
            loading="eager"
            data-testid="hero-image"
          />
          {/* Dual-direction gradient: deep on left for legible copy, soft on right to let art breathe */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/65 to-black/15" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent" />
          <HeroGoldOverlay density="medium" />
        </div>
        <div className="relative app-container py-24 md:py-32 lg:py-36">
          <div className="max-w-3xl">
            <div className="eyebrow mb-4" data-testid="hero-eyebrow">{t("home.hero_eyebrow")}</div>
            <h1
              className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-[68px] leading-[1.04] tracking-tight max-w-[22ch]"
              data-testid="hero-title"
            >
              <span className="text-white">{t("home.hero_l1_pre")}</span>
              <span className="gold-text">{t("home.hero_l1_acc")}</span>
              <br />
              <span className="text-white">{t("home.hero_l2_pre")}</span>
              <span className="gold-text">{t("home.hero_l2_acc")}</span>
              <br />
              <span className="text-white">{t("home.hero_l3_pre")}</span>
              <span className="gold-text">{t("home.hero_l3_acc")}</span>
            </h1>
            <p className="text-white/85 mt-7 leading-relaxed max-w-[58ch] body-copy text-lg md:text-xl">
              {t("home.hero_subtitle")}
            </p>
            <div className="mt-9 flex flex-wrap gap-3" data-testid="hero-ctas">
              <Link
                to="/dosha"
                className="btn-primary text-sm inline-flex items-center gap-2"
                data-testid="hero-cta-dosha"
              >
                <Sparkles className="w-4 h-4" />
                {t("home.hero_cta_dosha")}
              </Link>
              <Link
                to="/wellness"
                className="btn-ghost text-sm inline-flex items-center gap-2"
                data-testid="hero-cta-wellness"
              >
                {t("home.hero_cta_wellness")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-2" data-testid="hero-chips">
              {chips.slice(0, 5).map((c) => (
                <MotivationChip key={c.label} {...c} icon={Sparkles} testid={`hero-chip-${c.label.toLowerCase()}`} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* FIVE GATEWAYS — large visual ecosystem cards in priority order */}
      {/* ============================================================ */}
      <Section
        eyebrow={t("home.gateways_eyebrow")}
        title={t("home.gateways_title")}
        subtitle={t("home.gateways_subtitle")}
      >
        <div className="grid gap-4 md:gap-5 grid-cols-1 md:grid-cols-2 lg:grid-cols-3" data-testid="ecosystem-gateways">
          {gateways.map((g, i) => {
            const Icon = g.icon;
            const isLarge = i === 0; // make first card span 2 cols on lg+ for visual hierarchy
            return (
              <Link
                key={g.to}
                to={g.to}
                className={`card-surface overflow-hidden group flex flex-col hover:border-[#C8A24A]/45 transition-colors relative ${
                  isLarge ? "lg:col-span-2 lg:row-span-1" : ""
                }`}
                data-testid={`gateway-${g.to.replace("/", "")}`}
              >
                <div className={`overflow-hidden relative ${isLarge ? "aspect-[16/9] lg:aspect-[16/10]" : "aspect-[16/10]"}`}>
                  <img src={g.image} alt={g.title} className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.05]" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                  <div className="absolute top-4 left-4 eyebrow">{g.eyebrow}</div>
                  <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-black/55 border border-[#C8A24A]/55 backdrop-blur grid place-items-center flex-shrink-0">
                        <Icon className="w-5 h-5 gold-text" />
                      </div>
                      <div className="font-display text-2xl md:text-3xl leading-tight">{g.title}</div>
                    </div>
                    <div className="text-[#D4AF37] hidden md:flex items-center gap-1 text-xs uppercase tracking-[0.16em] opacity-90 group-hover:opacity-100 transition-opacity">
                      {t("home.gateway_enter")} <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <p className="text-sm text-white/72 leading-relaxed flex-1 body-copy max-w-[55ch]">{g.blurb}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </Section>

      {/* ============================================================ */}
      {/* ASK DONNY — Priority 6 — premium AI assistant tile */}
      {/* ============================================================ */}
      <Section eyebrow={t("home.ask_donny_eyebrow")} title={t("home.ask_donny_title")}>
        <Link
          to="/ask-donny"
          className="card-surface p-6 md:p-10 grid md:grid-cols-3 gap-6 items-center group hover:border-[#C8A24A]/45 transition-colors"
          data-testid="home-ask-donny"
        >
          <div className="md:col-span-2">
            <div className="text-xs tracking-[0.28em] uppercase gold-text mb-3 inline-flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> {t("home.ask_donny_meta_chip")}
            </div>
            <h3 className="font-display text-3xl md:text-4xl leading-tight max-w-[24ch]">
              {t("home.ask_donny_h")}
            </h3>
            <p className="text-white/72 mt-4 leading-relaxed max-w-[60ch] body-copy">
              {t("home.ask_donny_body")}
            </p>
            <div className="mt-5 inline-flex items-center gap-2 text-sm gold-text group-hover:underline">
              {t("nav.ask_donny")} <ArrowRight className="w-4 h-4" />
            </div>
          </div>
          <div className="relative aspect-square rounded-2xl overflow-hidden border border-[#C8A24A]/30 bg-gradient-to-br from-[#1A1A20] to-[#0A0A0F]">
            <img
              src={BRAND.DONNY_AVATAR}
              alt={t("home.ask_donny_avatar_alt")}
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
              data-testid="home-donny-avatar"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5">
              <div className="font-display text-2xl">Donny</div>
              <div className="text-[10px] tracking-[0.28em] gold-text uppercase mt-1">{t("home.ask_donny_avatar_eyebrow")}</div>
            </div>
          </div>
        </Link>
      </Section>

      {/* ============================================================ */}
      {/* MOTIVATION RIBBON — functional chips with modals */}
      {/* ============================================================ */}
      <Section
        eyebrow={t("home.motivation_eyebrow")}
        title={t("home.motivation_title")}
        subtitle={t("home.motivation_subtitle")}
      >
        <div className="flex flex-wrap gap-2" data-testid="motivation-chips">
          {chips.map((c) => (
            <MotivationChip
              key={c.label}
              label={c.label}
              quote={c.quote}
              ctaLabel={c.ctaLabel}
              ctaTo={c.ctaTo}
              icon={Sparkles}
              testid={`motivation-chip-${c.label.toLowerCase()}`}
            />
          ))}
        </div>
      </Section>

      {/* ============================================================ */}
      {/* MEMBER BENEFITS — featured offers strip */}
      {/* ============================================================ */}
      <MemberBenefitsStrip />

      {/* ============================================================ */}
      {/* FEATURED PRACTITIONERS — Marketplace spotlight */}
      {/* ============================================================ */}
      <PractitionerRecommendations
        eyebrow={`— ${t("nav.marketplace")}`}
        title={t("marketplace.featured_title")}
        subtitle={t("marketplace.featured_subtitle")}
        limit={3}
        testid="home-featured-practitioners"
      />

      {/* ============================================================ */}
      {/* APP DOWNLOAD — premium phone mockup + store badges */}
      {/* ============================================================ */}
      <AppDownloadSection />

      {/* ============================================================ */}
      {/* DOSHA — Priority 7 — single small strip, primary CTA is floating bubble */}
      {/* ============================================================ */}
      <Section eyebrow={t("home.dosha_eyebrow")} title={t("home.dosha_title")}>
        <div className="card-surface p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5 md:gap-8" data-testid="home-dosha-strip">
          <div className="flex-1">
            <p className="text-white/78 leading-relaxed max-w-[60ch] body-copy">
              {t("home.dosha_body")}
            </p>
          </div>
          <Link to="/dosha" className="btn-primary text-sm inline-flex items-center gap-2 whitespace-nowrap self-start md:self-auto" data-testid="home-dosha-cta">
            <Sparkles className="w-4 h-4" /> {t("dosha.cta_short")}
          </Link>
        </div>
      </Section>
    </div>
  );
}
